import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { gallery as galleryApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Header from "@/components/layout/Header";
import { parseDSL } from "@/lib/dsl-parser";
import DiagramRenderer from "@/components/editor/DiagramRenderer";
import { Search, ArrowRight } from "lucide-react";

const TEMPLATES = {
  "agent-workflow": `agentWorkflow
title ReAct Agent Loop
[start] Start
[task] Receive "Receive Task"
[task] Think "Think & Plan"
[decision] NeedTool "Need Tool?"
[task] SelectTool "Select Tool"
[task] Execute "Execute Tool"
[task] Observe "Observe Result"
[decision] Done "Task Done?"
[task] Respond "Generate Response"
[end] End
Start --> Receive
Receive --> Think
Think --> NeedTool
NeedTool --> SelectTool : yes
NeedTool --> Respond : no
SelectTool --> Execute
Execute --> Observe
Observe --> Think : loop
Done --> Respond : yes
Respond --> End`,
  flowchart: `flowchart
title User Auth Flow
A[User] --> B[Login Page]
B --> C{Valid?}
C --> D[Dashboard] : yes
C --> E[Error] : no
E --> B`,
  sequence: `sequence
title API Request Flow
Client -> Server: GET /api/data
Server -> DB: query
DB --> Server: result
Server --> Client: 200 OK`,
  "knowledge-graph": `knowledgeGraph
title AI Concepts
entity LLM [concept] "Large Language Model"
entity Transformer [concept] "Transformer"
entity Attention [concept] "Attention"
entity BERT [model]
entity GPT [model]
LLM -[based on]-> Transformer
Transformer -[uses]-> Attention
BERT -[is a]-> LLM
GPT -[is a]-> LLM`,
  pipeline: `pipeline
title RAG Pipeline
[input] Query "User Query"
[transform] Embed "Embed Query"
[process] Retrieve "Vector Retrieve"
[model] LLM "LLM Generation"
[output] Response "Final Response"
Query --> Embed
Embed --> Retrieve : vector search
Retrieve --> LLM : context + query
LLM --> Response`,
  "model-arch": `modelArch
title Transformer
layer Input [embedding] "Input Embedding"
layer Attn1 [attention] "Multi-Head Attention"
layer Norm1 [activation] "Layer Norm"
layer FFN [dense] "Feed-Forward"
layer Output [dense] "Softmax"
Input --> Attn1
Attn1 --> Norm1
Norm1 --> FFN
FFN --> Output`,
  gantt: `gantt
title Project Plan
dateFormat YYYY-MM-DD
section Design
Research : 2024-01-01, 7d
Mockups : 2024-01-08, 5d
section Dev
Backend : 2024-01-13, 10d
Frontend : 2024-01-20, 10d`,
  mindmap: `mindmap
title AI Systems
root[AI Systems]
  branch[Language Models]
    leaf[GPT-4]
    leaf[Claude]
    leaf[Llama]
  branch[Vision]
    leaf[DALL-E]
    leaf[Stable Diffusion]
  branch[Tools]
    leaf[RAG]
    leaf[Agents]`,
  pie: `pie
title Tech Stack
React: 35
Python: 30
TypeScript: 20
Other: 15`,
  er: `erDiagram
title User System
USERS ||--o{ ORDERS : places
ORDERS ||--|{ ORDER_ITEMS : contains
PRODUCTS ||--o{ ORDER_ITEMS : included_in
USERS {
  id UUID PK
  email VARCHAR
  name VARCHAR
}`,
};

const CATEGORIES = ["All", "AI/ML", "Architecture", "Data", "Project", "General"];
const CATEGORY_MAP = {
  "agent-workflow": "AI/ML", "ai-agent": "AI/ML", "dag": "AI/ML", "pipeline": "AI/ML",
  "knowledge-graph": "AI/ML", "model-arch": "AI/ML", "system-arch": "Architecture",
  "c4": "Architecture", "block": "Architecture", "class": "Architecture",
  "er": "Data", "sankey": "Data", "pie": "Data",
  "gantt": "Project", "kanban": "Project", "timeline": "Project", "requirements": "Project",
  "flowchart": "General", "sequence": "General", "mindmap": "General",
  "journey": "General", "state": "General", "git": "General",
};

function DiagramPreviewCard({ item, onClone }) {
  const code = TEMPLATES[item.type] || `flowchart\ntitle ${item.label}\nA[Start] --> B[End]`;
  const parsed = (() => { try { return parseDSL(code); } catch { return null; } })();

  return (
    <div
      className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-400 hover:shadow-lg transition-all duration-200"
      data-testid={`gallery-card-${item.type}`}
    >
      <div className="h-[180px] bg-slate-50 relative overflow-hidden border-b border-slate-100">
        <div className="absolute inset-0 scale-75 origin-top-left" style={{ width: "133%", height: "133%" }}>
          {parsed ? (
            <DiagramRenderer graph={parsed} />
          ) : (
            <div className="h-full flex items-center justify-center text-slate-300 text-xs">Preview unavailable</div>
          )}
        </div>
        <div className="absolute inset-0 bg-transparent group-hover:bg-black/5 transition-colors" />
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-semibold text-slate-900 text-sm font-heading">{item.label}</h3>
          <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full font-medium">{CATEGORY_MAP[item.type] || "General"}</span>
        </div>
        <p className="text-xs text-slate-500 font-body mb-3 line-clamp-2">{item.description}</p>
        <Button
          size="sm"
          className="w-full gap-1.5 h-8 text-xs"
          onClick={() => onClone(item.type, code)}
          data-testid={`clone-template-${item.type}`}
        >
          Use Template <ArrowRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export default function Gallery() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    galleryApi.list().then(res => {
      setItems(res.data);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(item => {
    const matchSearch = !search || item.label.toLowerCase().includes(search.toLowerCase()) || item.description.toLowerCase().includes(search.toLowerCase());
    const matchCategory = category === "All" || CATEGORY_MAP[item.type] === category;
    return matchSearch && matchCategory;
  });

  const handleClone = (type, code) => {
    navigate("/editor", { state: { code, type } });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 font-heading mb-3">Template Gallery</h1>
          <p className="text-slate-500 font-body text-lg max-w-xl mx-auto">
            Explore all 25 diagram types. Pick a template to get started instantly.
          </p>
        </div>

        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8 items-center justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search templates..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 font-body"
              data-testid="gallery-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap justify-center">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                data-testid={`gallery-filter-${cat}`}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  category === cat
                    ? "bg-slate-900 text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl h-[280px] animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-400 mb-4 font-body">{filtered.length} template{filtered.length !== 1 ? "s" : ""}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="gallery-grid">
              {filtered.map(item => (
                <DiagramPreviewCard key={item.type} item={item} onClone={handleClone} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
